import { useState, useEffect } from 'react';
import { reject } from 'lodash';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useSignUpModal } from 'components/SignUpModal';
import { SaveToModal } from 'components/SaveToModal/SaveToModal';
import { trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import { ListOrProjectWithStages } from 'lib/types';

interface Props {
  userId: number;
  savedToListsWithStages: ListOrProjectWithStages[];
  savedToProjectsWithStages: ListOrProjectWithStages[];
  onListsUpdated?: () => void;
}

export const SaveCreatorButton = ({
  userId, savedToListsWithStages, savedToProjectsWithStages, onListsUpdated,
}: Props) => {
  const currentUser = useCurrentUser();
  const { showSignUpModal } = useSignUpModal();

  const [lists, setLists] = useState<ListOrProjectWithStages[]>(savedToListsWithStages || []);
  const [projects, setProjects] = useState<ListOrProjectWithStages[]>(
    savedToProjectsWithStages || [],
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const API = useApi();

  // Sync state with props when they change (e.g., after fetching updated list details)
  useEffect(() => {
    setLists(savedToListsWithStages || []);
  }, [savedToListsWithStages]);

  useEffect(() => {
    setProjects(savedToProjectsWithStages || []);
  }, [savedToProjectsWithStages]);

  const savedToList = (id: number) => {
    setLists([...lists, { id }]);
    setSaveOpen(false);
    onListsUpdated?.();
  };

  const removedFromList = (id: number) => {
    setLists(reject(lists, (n) => n.id === id));
    setSaveOpen(false);
    onListsUpdated?.();
  };

  const savedToProject = (id: number) => {
    setProjects([...projects, { id, stage: undefined, trigger: undefined }]);
    setSaveOpen(false);
    onListsUpdated?.();
  };

  const removedFromProject = (id: number) => {
    setProjects(reject(projects, (n) => n.id === id));
    setSaveOpen(false);
    onListsUpdated?.();
  };

  const click = () => {
    if (!currentUser) {
      trackClickSignupEvent(API, 'save-creator-to-list');
      showSignUpModal('Please sign up or sign in to save creators');
      return;
    }
    setSaveOpen(true);
  };

  const buttonToDisplay = () => {
    if (!currentUser) {
      return null;
    }
    const totalCount = (lists?.length || 0) + (projects?.length || 0);
    if (totalCount > 1) {
      return (
        <div className="relative">
          <div className="absolute -top-1 -right-0 font-bold bg-white dark:bg-appbg-dark rounded-full px-1 text-sm">{totalCount}</div>
          <BookmarksIcon />
        </div>
      );
    }
    if (totalCount === 1) {
      return <BookmarkAddedIcon />;
    }
    return <BookmarkAddOutlinedIcon />;
  };

  return (
    <div className="text-orange">
      {saveOpen && (
        <SaveToModal
          userId={userId}
          listsWithStages={lists}
          projectsWithStages={projects}
          onAddToList={savedToList}
          onRemoveFromList={removedFromList}
          onAddToProject={savedToProject}
          onRemoveFromProject={removedFromProject}
          onCancel={() => setSaveOpen(false)}
        />
      )}
      <button
        type="button"
        onClick={click}
        aria-label="save or unsave user"
        color="inherit"
        title="Save or unsave user"
        className="hover:bg-hintgray hover:dark:bg-darkgray/50 rounded-full"
      >
        {buttonToDisplay()}
      </button>
    </div>
  );
};
